/**
 * NotificationPopupDialog - Popup modal for notifications
 *
 * Matches Xamarin NotificationListPopUp.xaml + NotificationListMobile.xaml (IsPage=false mode):
 *
 * NotificationListPopUp.xaml:
 * - popup:PopupPage overlay (semi-transparent background)
 * - Phone: <controls:NotificationListMobile Padding="20,50,20,50"/>
 *   → padding: left=20px, top=50px, right=20px, bottom=50px
 *
 * NotificationListMobile.xaml (Frame container):
 * - Frame: VerticalOptions=Center, HorizontalOptions=Center, HasShadow=False,
 *   CornerRadius=8, BackgroundColor=White, Padding="10,10,10,10"
 *
 * Grid rows (when IsPage=false):
 * - Row 0 (Auto): Grid with "Notification" title + "Close" button
 *   - "Notification": PoppinsBold, FontSize=14(Phone), Margin="0,10,10,10",
 *     HorizontalOptions=CenterAndExpand, TextColor=#243743 (TaskDetailsTextColor)
 *   - "Close": PoppinsRegular, FontSize=12(Phone), TextColor=#7F98B0 (TaskCardMobileFrameColor),
 *     HorizontalOptions=End
 * - Row 1 (Auto): Filter tabs CollectionView, HeightRequest=40, Margin="0,10,0,0"
 * - Row 2 (*): EmptyListView HeightRequest=500 OR CollectionView Margin="0,10,0,0",
 *   VerticalScrollBarVisibility=Always, ItemSpacing=5
 * - Row 3 (Auto): "See All Notifications" Label, TextDecorations=Underline,
 *   HorizontalOptions=Center
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES, NOTIFICATION_FILTER } from '@/constants';
import { useNotificationsQuery } from '@/hooks/dashboard';
import { setLastNotificationTime, clearUnreadNotificationIds } from '@/utils';

import { EmptyNotificationState } from './EmptyNotificationState';
import { NotificationFilterTabs } from './NotificationFilterTabs';
import { TaskNotificationCard } from './TaskNotificationCard';
import { TrailerNotificationCard } from './TrailerNotificationCard';

import type { NotificationFilterType } from '@/constants';
import type { NotificationModel } from '@/types';

const FILTER_OPTIONS: NotificationFilterType[] = [
  NOTIFICATION_FILTER.ALL,
  NOTIFICATION_FILTER.TASK,
  NOTIFICATION_FILTER.TRAILER,
];

interface NotificationPopupDialogProps {
  open: boolean;
  onClose: () => void;
}

export function NotificationPopupDialog({ open, onClose }: NotificationPopupDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedFilter, setLocalFilter] = useState<NotificationFilterType>(
    NOTIFICATION_FILTER.ALL
  );

  // Matches Xamarin NotificationListPopUpViewModel.GetNotifications — real API call
  const { data: notifications = [], isLoading } = useNotificationsQuery(selectedFilter, open);

  // Matches Xamarin Destroy — save last notification time on close, clear unreads
  const handleClose = useCallback(() => {
    // Matches Xamarin NotificationListPopUpViewModel.Destroy():
    // PreferencesService.SetLastNotificationTime(DateTime.Now)
    setLastNotificationTime(new Date().toISOString());
    // Clear in-memory unread IDs so bell badge resets
    clearUnreadNotificationIds();
    onClose();
  }, [onClose]);

  // Matches Xamarin SelectedFilterCommandHandler
  const handleFilterChange = useCallback((filter: NotificationFilterType) => {
    setLocalFilter(filter);
  }, []);

  // Matches Xamarin NavigateToUpcomingTasksHandler
  const handleNotificationPress = useCallback(
    (notification: NotificationModel) => {
      handleClose();
      if (notification.notificationType === 'Task') {
        navigate(ROUTES.dashboard);
      } else {
        navigate(ROUTES.trailers);
      }
    },
    [navigate, handleClose]
  );

  // Matches Xamarin SeeAllNotifications command — navigates to full NotificationListPage
  const handleSeeAll = useCallback(() => {
    handleClose();
    navigate(ROUTES.notifications);
  }, [navigate, handleClose]);

  // Close on Escape key — accessibility requirement
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, handleClose]);

  if (!open) return null;

  const isEmpty = notifications.length === 0;

  return (
    /* Matches Xamarin PopupPage — full-screen overlay with semi-transparent background */
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 animate-in fade-in duration-150"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Matches Xamarin NotificationListMobile Padding="20,50,20,50" (left=20,top=50,right=20,bottom=50)
          This is the padding around the white frame within the popup page */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className="flex h-full w-full flex-col px-[20px] py-[50px]"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Matches Xamarin Frame: VerticalOptions=Center, HorizontalOptions=Center,
            CornerRadius=8, BackgroundColor=White, Padding="10,10,10,10", HasShadow=False */}
        <div className="flex flex-1 flex-col rounded-[8px] bg-white p-[10px] animate-in zoom-in-95 duration-150">
          {/* Row 0: Title + Close — Matches Xamarin Grid Row=0 (IsPage=false) */}
          <div className="flex items-center">
            {/* Matches Xamarin: Label "Notification", PoppinsBold, FontSize=14(Phone),
                Margin="0,10,10,10" (left=0,top=10,right=10,bottom=10),
                HorizontalOptions=CenterAndExpand, TextColor=#243743 */}
            <p
              className="flex-1 text-center font-poppins text-[24px] font-bold text-[#243743]"
              style={{ margin: '10px 10px 10px 0' }}
            >
              {t('notification.title')}
            </p>
            {/* Matches Xamarin: Label "Close", PoppinsRegular, FontSize=12(Phone),
                TextColor=#7F98B0 (TaskCardMobileFrameColor), HorizontalOptions=End */}
            <button
              onClick={handleClose}
              className="shrink-0 font-poppins text-[16px] text-[#7F98B0]"
            >
              {t('common.close')}
            </button>
          </div>

          {/* Row 1: Filter tabs — Matches Xamarin CollectionView filterList Grid.Row=1,
              HeightRequest=40, Margin="0,10,0,0" */}
          <div className="mt-[10px]">
            <NotificationFilterTabs
              filters={FILTER_OPTIONS}
              selectedFilter={selectedFilter}
              onFilterChange={handleFilterChange}
            />
          </div>

          {/* Row 2: Notification list (scrollable, fills remaining space)
              Matches Xamarin: CollectionView Grid.Row=2 (Height="*"), Margin="0,10,0,0",
              VerticalScrollBarVisibility=Always, ItemSpacing=5
              EmptyListView: HeightRequest=500 */}
          <div className="mt-[10px] flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <span className="font-poppins text-base font-medium text-[#243742]">
                  {t('common.loading')}
                </span>
                <div className="flex items-center gap-3">
                  <span className="h-2 w-2 animate-bounce rounded-full bg-purple-500 [animation-delay:-0.3s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-purple-500 [animation-delay:-0.15s]" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-purple-500" />
                  <span className="h-2 w-2 animate-bounce rounded-full bg-purple-500 [animation-delay:0.15s]" />
                </div>
              </div>
            )}

            {!isLoading && isEmpty && <EmptyNotificationState />}

            {!isLoading && !isEmpty && (
              <div className="flex flex-col gap-[5px]">
                {notifications.map((notification) => {
                  if (notification.isShowTaskNotification) {
                    return (
                      <TaskNotificationCard
                        key={notification.id}
                        notification={notification}
                        onPress={handleNotificationPress}
                      />
                    );
                  }
                  if (notification.isShowTrailerNotification) {
                    return (
                      <TrailerNotificationCard
                        key={notification.id}
                        notification={notification}
                        onPress={handleNotificationPress}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>

          {/* Row 3: "See All Notifications" — Matches Xamarin Grid.Row=3,
              Label TextDecorations=Underline, HorizontalOptions=Center (IsPage=false only) */}
          <div className="py-[10px]">
            <button onClick={handleSeeAll} className="mx-auto block font-poppins text-sm underline">
              {t('notification.seeAllNotifications')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
